import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { admin } from '../api'
import { formatBytes, parseBytes } from '../format'

/**
 * Grants one folder to one account, from the file browser.
 *
 * Typing a path into the admin form works, but assigning the folder you are
 * looking at is how this is actually used: hand a family member their corner of
 * the disk while browsing it.
 */
export default function AssignFolderDialog({
  nodeId,
  path,
  folderName,
  onClose,
}: {
  nodeId: string
  path: string
  folderName: string
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const users = useQuery({ queryKey: ['admin', 'users'], queryFn: admin.users })
  const shares = useQuery({ queryKey: ['admin', 'shares'], queryFn: admin.shares })

  const [userId, setUserId] = useState('')
  const [permission, setPermission] = useState<'read' | 'write'>('write')
  const [label, setLabel] = useState(folderName)
  const [quota, setQuota] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  /** Who already has this exact folder, so it is not granted twice. */
  const existing = (shares.data ?? []).filter(
    (share) => share.nodeId === nodeId && share.path === path,
  )
  const candidates = (users.data ?? []).filter(
    (user) => user.role !== 'admin' && !existing.some((share) => share.userId === user.id),
  )

  const assign = useMutation({
    mutationFn: () => {
      const quotaBytes = quota.trim() === '' ? null : parseBytes(quota)
      if (quota.trim() !== '' && quotaBytes === null) {
        throw new Error('용량은 "50GB" 같은 형식으로 입력하세요')
      }
      return admin.createShare({
        userId,
        nodeId,
        path,
        label: label.trim(),
        permission,
        quotaBytes,
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'shares'] })
      setDone(true)
      setUserId('')
    },
    onError: (err: Error) => setError(err.message),
  })

  const revoke = useMutation({
    mutationFn: (id: string) => admin.deleteShare(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'shares'] }),
    onError: (err: Error) => setError(err.message),
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setDone(false)
    assign.mutate()
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="assign-title">
        <h2 id="assign-title">폴더 할당</h2>
        <p className="mono conflict-name">{path}</p>

        {existing.length > 0 && (
          <>
            <p className="muted small">이미 이 폴더를 가진 사람</p>
            <ul className="assigned">
              {existing.map((share) => (
                <li key={share.id}>
                  <span>
                    {share.userDisplayName}
                    <span className="muted small">
                      {' '}
                      · {share.permission === 'read' ? '읽기 전용' : '읽기·쓰기'}
                      {share.quotaBytes !== null && ` · ${formatBytes(share.quotaBytes)}`}
                    </span>
                  </span>
                  <button type="button" className="link danger" onClick={() => revoke.mutate(share.id)}>
                    회수
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}

        {candidates.length === 0 ? (
          <p className="muted small">
            {users.data?.some((user) => user.role !== 'admin')
              ? '이 폴더는 이미 모든 사용자에게 할당돼 있습니다.'
              : '먼저 관리 → 사용자에서 계정을 만드세요.'}
          </p>
        ) : (
          <form onSubmit={submit} className="stack">
            <label>
              사용자
              <select value={userId} onChange={(event) => setUserId(event.target.value)} required>
                <option value="">선택</option>
                {candidates.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.displayName} ({user.email})
                  </option>
                ))}
              </select>
            </label>

            <label>
              권한
              <select
                value={permission}
                onChange={(event) => setPermission(event.target.value as 'read' | 'write')}
              >
                <option value="write">읽기·쓰기</option>
                <option value="read">읽기 전용</option>
              </select>
            </label>

            <label>
              사용자에게 보일 이름
              <input value={label} onChange={(event) => setLabel(event.target.value)} />
            </label>

            <label>
              용량 제한 (비우면 무제한)
              <input
                placeholder="예: 50GB"
                value={quota}
                onChange={(event) => setQuota(event.target.value)}
              />
            </label>

            {error && <p className="error">{error}</p>}
            {done && <p className="success">할당했습니다.</p>}

            <div className="modal-actions">
              <button type="submit" className="primary" disabled={assign.isPending}>
                할당
              </button>
              <button type="button" onClick={onClose}>
                닫기
              </button>
            </div>
          </form>
        )}

        {candidates.length === 0 && (
          <div className="modal-actions">
            <button type="button" onClick={onClose}>
              닫기
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
